import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import type { WishlistVisibility } from '@nexus/api-types';
import type { WishlistCreateDraft, WishlistCreateFieldErrors } from '@nexus/customer/wishlists/model';
import {
  UiButtonComponent,
  UiChoiceGroupComponent,
  UiIconButtonComponent,
  UiInputComponent,
  type UiChoiceOption,
} from '@nexus/frontend-shared-ui';

@Component({
  selector: 'wishlist-create-visibility-selector',
  imports: [UiButtonComponent, UiChoiceGroupComponent, UiIconButtonComponent, UiInputComponent],
  templateUrl: './wishlist-create-visibility-selector.component.html',
  styleUrl: './wishlist-create-visibility-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WishlistCreateVisibilitySelectorComponent {
  public readonly draft: InputSignal<WishlistCreateDraft> = input.required<WishlistCreateDraft>();
  public readonly errors: InputSignal<WishlistCreateFieldErrors> = input.required<WishlistCreateFieldErrors>();
  public readonly invitedEmailDraft: InputSignal<string> = input<string>('');
  public readonly invitedEmailError: InputSignal<string | null> = input<string | null>(null);
  public readonly visibilityChange: OutputEmitterRef<WishlistVisibility> = output<WishlistVisibility>();
  public readonly invitedEmailDraftChange: OutputEmitterRef<string> = output<string>();
  public readonly invitedEmailAddSelected: OutputEmitterRef<void> = output<void>();
  public readonly invitedEmailRemoveSelected: OutputEmitterRef<string> = output<string>();

  protected readonly options: Signal<readonly UiChoiceOption[]> = computed(
    (): readonly UiChoiceOption[] => [
      {
        id: 'private',
        label: 'Tylko ja',
        description: 'Lista jest prywatna i widoczna tylko dla Ciebie.',
        iconLabel: 'L',
      },
      {
        id: 'household',
        label: 'Domownicy',
        description: 'Widzą ją osoby z aktywnego domu.',
        iconLabel: 'H',
      },
      {
        id: 'invited_only',
        label: 'Zaproszone osoby',
        description: 'Dostęp mają tylko osoby dodane przez email.',
        iconLabel: '@',
      },
      {
        id: 'anyone_with_link',
        label: 'Każdy z linkiem',
        description: 'Link włączysz i skopiujesz po utworzeniu wishlisty.',
        iconLabel: '#',
      },
    ],
  );

  protected handleVisibilitySelected(visibility: WishlistVisibility): void {
    this.visibilityChange.emit(visibility);
  }

  protected handleVisibilityOptionSelected(optionId: string): void {
    if (!isWishlistVisibility(optionId)) {
      return;
    }

    this.handleVisibilitySelected(optionId);
  }

  protected handleInvitedEmailDraftChange(value: string): void {
    this.invitedEmailDraftChange.emit(value);
  }

  protected handleInvitedEmailAddSelected(): void {
    this.invitedEmailAddSelected.emit();
  }

  protected handleInvitedEmailRemoveSelected(email: string): void {
    this.invitedEmailRemoveSelected.emit(email);
  }
}

function isWishlistVisibility(value: string): value is WishlistVisibility {
  return value === 'private' || value === 'household' || value === 'invited_only' || value === 'anyone_with_link';
}
